import React, { useState } from 'react'

import { Info, ArrowForward } from '@mui/icons-material'
import {
  Box,
  Chip,
  Typography,
  Popover,
  IconButton,
  Divider,
  Paper
} from '@mui/material'

type StatusInfo = {
  status: string
  color: 'success' | 'primary' | 'secondary' | 'info' | 'warning' | 'error' | 'default'
  description: string
  isEndState?: boolean
}

// Main workflow progression
const normalFlowStatuses: StatusInfo[] = [
  {
    status: 'PURCHASED',
    color: 'warning',
    description: 'Package purchased by user'
  },
  {
    status: 'CREATED',
    color: 'info',
    description: 'Order submitted to Junction Labs'
  },
  {
    status: 'RECEIVED',
    color: 'secondary',
    description: 'Requisition form ready, can book appointments'
  },
  {
    status: 'IN_PROGRESS',
    color: 'primary',
    description: 'Lab processing order (sample collecting or awaiting results)'
  },
  {
    status: 'COMPLETED',
    color: 'success',
    description: 'Results received, report generated if profile complete',
    isEndState: true
  }
]

// Error/alternative states
const alternativeStatuses: StatusInfo[] = [
  {
    status: 'UNKNOWN',
    color: 'default',
    description: 'Status is unknown (most likely an issue on our server)'
  },
  {
    status: 'FAILED',
    color: 'error',
    description: 'Order processing failed (on Junction side)',
    isEndState: true
  },
  {
    status: 'CANCELLED',
    color: 'error',
    description: 'Order was cancelled (on Junction side or by user)',
    isEndState: true
  },
  {
    status: 'DELETED',
    color: 'default',
    description: 'Order soft-deleted by admin, hidden from client',
    isEndState: true
  }
]

type AppointmentStatusInfo = {
  status: string
  color: 'success' | 'primary' | 'error' | 'default'
  description: string
  isEndState?: boolean
}

// PSC Appointment workflow
const appointmentFlowStatuses: AppointmentStatusInfo[] = [
  {
    status: 'NONE',
    color: 'default',
    description: 'No appointment scheduled'
  },
  {
    status: 'SCHEDULED',
    color: 'success',
    description: 'Appointment booked'
  },
  {
    status: 'CONFIRMED',
    color: 'success',
    description: 'Appointment confirmed'
  },
  {
    status: 'COMPLETED',
    color: 'primary',
    description: 'Appointment completed',
    isEndState: true
  }
]

const appointmentAlternativeStatuses: AppointmentStatusInfo[] = [
  {
    status: 'CANCELLED',
    color: 'error',
    description: 'Appointment cancelled',
    isEndState: true
  }
]

const StatusFlow = ({
  statuses,
  title,
  showArrows = true
}: {
  statuses: StatusInfo[] | AppointmentStatusInfo[]
  title: string
  showArrows?: boolean
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{
      p: 1,
      bgcolor: 'grey.50',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider'
    }}>
      {showArrows ? (
        // Linear flow with arrows between chips
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap'
        }}>
          {statuses.map((info, index) => (
            <Box key={info.status} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={info.status}
                  color={info.color}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: '80px',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    ...(info.isEndState && {
                      borderWidth: 2,
                      fontWeight: 600
                    })
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.65rem',
                    textAlign: 'center',
                    maxWidth: '100px',
                    lineHeight: 1.1,
                    minHeight: '20px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {info.description}
                </Typography>
              </Box>

              {/* Arrow centered at chip level */}
              {index < statuses.length - 1 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px', // Match small chip height
                  mt: 0
                }}>
                  <ArrowForward
                    sx={{
                      color: 'text.secondary',
                      fontSize: 12
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        // Simple list without arrows
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          flexWrap: 'wrap'
        }}>
          {statuses.map((info) => (
            <Box key={info.status} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={info.status}
                color={info.color}
                size="small"
                variant="outlined"
                sx={{
                  minWidth: '80px',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  ...(info.isEndState && {
                    borderWidth: 2,
                    fontWeight: 600
                  })
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.65rem',
                  textAlign: 'center',
                  maxWidth: '100px',
                  lineHeight: 1.1,
                  minHeight: '20px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {info.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  </Box>
)

export default function StatusInfoButton() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton
        aria-label="Status info"
        onClick={handleClick}
        size="small"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: 3,
          '&:hover': {
            bgcolor: 'primary.dark',
            boxShadow: 6
          },
          zIndex: 1000
        }}
      >
        <Info />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxWidth: 600,
            maxHeight: 500,
            overflow: 'auto'
          }
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Status Guide
          </Typography>

          <StatusFlow
            statuses={normalFlowStatuses}
            title="Normal Lab Order Flow"
            showArrows={true}
          />

          <StatusFlow
            statuses={alternativeStatuses}
            title="Alternative States"
            showArrows={false}
          />

          <Divider sx={{ my: 2 }} />

          <StatusFlow
            statuses={appointmentFlowStatuses}
            title="PSC Appointment Flow"
            showArrows={true}
          />

          <StatusFlow
            statuses={appointmentAlternativeStatuses}
            title="Appointment Alternative"
            showArrows={false}
          />

          <Box sx={{ mt: 2, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
            <Typography variant="caption" color="info.main" fontWeight={600}>
              💡 Note:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Statuses with thick borders are end states. PSC appointments are only relevant for Quest lab orders.
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </>
  )
}